import { createSlice, current } from "@reduxjs/toolkit";

import * as Act from "./actions";

const initialState = {
  loading: false,
  settings: null,

  gameLoading: false,
  gameSettings: null,

  bombCountLoading: false,
  bombCountSettings: null,

  weeklyRewards: [],
  totalWeeklyRewards: 0,
  weeklyRewardDetail: null,

  exportRequestLoading: false,
  exportRequestList: [],
  exportRequestCount: 0,

  coinHistoryLoading: false,
  coinHistoryList: [],
  coinHistoryCount: 0,
};

const slice = createSlice({
  name: "setting",
  initialState: { ...initialState },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(Act.getSettings.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(Act.getSettings.fulfilled, (state, { payload }) => ({
      ...state,
      loading: false,
      settings: payload,
    }));
    builder.addCase(Act.getSettings.rejected, (state, action) => ({
      ...state,
      loading: false,
      settings: null,
    }));

    builder.addCase(Act.getGameSetting.pending, (state, action) => ({
      ...state,
      gameLoading: true,
    }));
    builder.addCase(Act.getGameSetting.fulfilled, (state, { payload }) => {
      // Support legacy payloads shaped as { game: {...} } or { data: {...} }
      const gameData = payload?.game || payload?.data || payload;

      return {
        ...state,
        gameLoading: false,
        gameSettings: gameData,
      };
    });
    builder.addCase(Act.getGameSetting.rejected, (state, action) => {
      console.error("getGameSetting rejected:", action.payload);
      return {
        ...state,
        gameLoading: false,
        gameSettings: null,
      };
    });

    builder.addCase(Act.getBombLineCount.pending, (state, action) => {
      state.bombCountLoading = true;
    });

    builder.addCase(Act.getBombLineCount.fulfilled, (state, { payload }) => {
      // Payload will be: { data: [...] }
      state.bombCountLoading = false;
      state.bombCountSettings = payload;
    });

    builder.addCase(Act.getBombLineCount.rejected, (state, action) => {
      console.error(
        "getBombLineCount rejected:",
        action.payload || action.error,
      );

      state.bombCountLoading = false;
      state.bombCountSettings = { data: [] };
    });

    builder.addCase(Act.updateSettings.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(Act.updateSettings.fulfilled, (state, { payload }) => ({
      ...state,
      loading: false,
      settings: payload,
    }));
    builder.addCase(Act.updateSettings.rejected, (state, action) => ({
      ...state,
      loading: false,
      settings: null,
    }));
    builder.addCase(Act.updateGameSettings.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(Act.updateGameSettings.fulfilled, (state, { payload }) => ({
      ...state,
      loading: false,
      settings: payload,
    }));
    builder.addCase(Act.updateGameSettings.rejected, (state, action) => ({
      ...state,
      loading: false,
      settings: null,
    }));
    builder.addCase(
      Act.updateWelcomeBonusSettings.pending,
      (state, action) => ({
        ...state,
        loading: true,
      }),
    );
    builder.addCase(
      Act.updateWelcomeBonusSettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(
      Act.updateWelcomeBonusSettings.rejected,
      (state, action) => ({
        ...state,
        loading: false,
        settings: null,
      }),
    );

    builder.addCase(
      Act.updateCryptoExchangeSettings.pending,
      (state, action) => ({
        ...state,
        loading: true,
      }),
    );
    builder.addCase(
      Act.updateCryptoExchangeSettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(
      Act.updateCryptoExchangeSettings.rejected,
      (state, action) => ({
        ...state,
        loading: false,
        settings: null,
      }),
    );

    builder.addCase(
      Act.updateCryptoDepositSettings.pending,
      (state, action) => ({
        ...state,
        loading: true,
      }),
    );
    builder.addCase(
      Act.updateCryptoDepositSettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(
      Act.updateCryptoDepositSettings.rejected,
      (state, action) => ({
        ...state,
        loading: false,
        settings: null,
      }),
    );

    builder.addCase(Act.updateEnergySettings.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(
      Act.updateEnergySettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(Act.updateEnergySettings.rejected, (state, action) => ({
      ...state,
      loading: false,
      settings: null,
    }));
    builder.addCase(Act.updateLeaderboardSettings.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(
      Act.updateLeaderboardSettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(
      Act.updateLeaderboardSettings.rejected,
      (state, action) => ({
        ...state,
        loading: false,
        settings: null,
      }),
    );

    builder.addCase(Act.updateConversionSettings.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(
      Act.updateConversionSettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(Act.updateConversionSettings.rejected, (state, action) => ({
      ...state,
      loading: false,
      settings: null,
    }));

    builder.addCase(Act.updatePromotionSettings.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(
      Act.updatePromotionSettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(Act.updatePromotionSettings.rejected, (state, action) => ({
      ...state,
      loading: false,
      settings: null,
    }));

    builder.addCase(Act.updatePaymentSettings.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(
      Act.updatePaymentSettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(Act.updatePaymentSettings.rejected, (state, action) => ({
      ...state,
      loading: false,
      settings: null,
    }));


      builder.addCase(Act.updateBuyEnergySettings.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(
      Act.updateBuyEnergySettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(Act.updateBuyEnergySettings.rejected, (state, action) => ({
      ...state,
      loading: false,
      settings: null,
    }));


    builder.addCase(
      Act.updateRewardSystemSettings.pending,
      (state, action) => ({
        ...state,
        loading: true,
      }),
    );
    builder.addCase(
      Act.updateRewardSystemSettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(
      Act.updateRewardSystemSettings.rejected,
      (state, action) => ({
        ...state,
        loading: false,
        settings: null,
      }),
    );

    builder.addCase(Act.updateJackpotSettings.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(
      Act.updateJackpotSettings.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        settings: payload,
      }),
    );
    builder.addCase(Act.updateJackpotSettings.rejected, (state, action) => ({
      ...state,
      loading: false,
      settings: null,
    }));

    builder.addCase(Act.getWeeklyRewardHistory.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(Act.getWeeklyRewardHistory.fulfilled, (state, action) => {
      state.weeklyRewards = action.payload.data;
      state.totalWeeklyRewards = action.payload.totalcount;
      state.loading = false;
    });
    builder.addCase(Act.getWeeklyRewardHistory.rejected, (state, action) => {
      state.loading = false;
    });

    builder.addCase(Act.getWeeklyRewardDetails.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(Act.getWeeklyRewardDetails.fulfilled, (state, action) => {
      state.weeklyRewardDetail = action.payload.data;
      state.loading = false;
    });
    builder.addCase(Act.getWeeklyRewardDetails.rejected, (state, action) => {
      state.loading = false;
    });

    builder.addCase(Act.updateWeeklyRewardHistory.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(
      Act.updateWeeklyRewardHistory.fulfilled,
      (state, { payload }) => ({
        ...state,
        loading: false,
        weeklyRewardDetail: payload,
      }),
    );
    builder.addCase(
      Act.updateWeeklyRewardHistory.rejected,
      (state, action) => ({
        ...state,
        loading: false,
        weeklyRewardDetail: null,
      }),
    );

    builder.addCase(Act.recalculateRewards.pending, (state, action) => ({
      ...state,
      loading: true,
    }));
    builder.addCase(Act.recalculateRewards.fulfilled, (state, { payload }) => ({
      ...state,
      loading: false,
      weeklyRewardDetail: payload,
    }));
    builder.addCase(Act.recalculateRewards.rejected, (state, action) => ({
      ...state,
      loading: false,
      weeklyRewardDetail: null,
    }));

    builder.addCase(Act.getExportRequestList.pending, (state, action) => ({
      ...state,
      exportRequestLoading: true,
    }));
    builder.addCase(
      Act.getExportRequestList.fulfilled,
      (state, { payload }) => ({
        ...state,
        exportRequestLoading: false,
        exportRequestList: payload.data,
        totalCount: payload.totalcount,
      }),
    );
    builder.addCase(Act.getExportRequestList.rejected, (state, action) => ({
      ...state,
      exportRequestLoading: false,
      exportRequestList: [],
      exportRequestCount: 0,
    }));

    builder.addCase(Act.getCoinHistoryList.pending, (state, action) => ({
      ...state,
      coinHistoryLoading: true,
    }));
    builder.addCase(Act.getCoinHistoryList.fulfilled, (state, { payload }) => ({
      ...state,
      coinHistoryLoading: false,
      coinHistoryList: payload.data,
      totalCount: payload.totalcount,
    }));
    builder.addCase(Act.getCoinHistoryList.rejected, (state, action) => ({
      ...state,
      coinHistoryLoading: false,
      coinHistoryList: [],
      coinHistoryCount: 0,
    }));
  },
});

export default slice;
